import React, { Component } from "react";
import { Button, Typography, Box } from "@mui/material";
import * as Sentry from "@sentry/browser";
import { connect } from "react-redux";
import { UserContext } from "../interfaces/UserContext";

interface Props {
  children: React.ReactNode;
  user?: UserContext;
}

interface State {
  hasError: boolean;
  hasRetried: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, hasRetried: false };
  }

  static getDerivedStateFromError = () => ({ hasError: true });

  componentDidCatch = (error, errorInfo) => {
    const { hasRetried } = this.state;
    const { user } = this.props;
    if (hasRetried) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: true });

    Sentry.withScope((scope) => {
      scope.setTag("component", "<ErrorBoundary />");
      scope.setLevel(Sentry.Severity.Critical);
      scope.setUser({
        id: user?.user?.id?.toString(),
        username: user?.userCreds?.username,
        email: user?.user?.email
      });
      scope.setExtras({
        userRole: user?.userCreds?.authenticable,
        resourceCentreId: user?.resourceCentre?.id,
        resourceCentreName: user?.resourceCentre?.name
      });
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  };

  resetState = () => {
    this.setState({ hasError: false, hasRetried: true });
  };

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <Box
          component="div"
          display="flex"
          height="100vh"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Typography variant="h6" component="h1" align="center">
            Oops! something went wrong.
          </Typography>
          <Button color="primary" onClick={this.resetState}>
            Try again
          </Button>
        </Box>
      );
    }

    return children || null;
  }
}

function mapStateToProps(state) {
  return {
    user: state.userContext
  };
}

export default connect(mapStateToProps, null)(ErrorBoundary);
